import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { B, radius, space, font, weight } from '../../constants/brand';
// The block parser lives in @harvus/shared because iOS renders these same
// answers; see shared/src/chatFormat.ts for what is deliberately NOT parsed.
import { parseMessageBody } from '@harvus/shared';
import { api } from '../../api/client';
import { Btn, Badge, ErrorMsg, Chip, Icon, Avatar } from '../ui';
import { InlineSearchPicker, useDealPickerItems } from '../tasks/TaskComponents';
import { AssistantDraftCard } from './AssistantDraftCard';
import { exportConversationPdf } from '../../utils/exportDownload';
import { groupThreadsByDate, filterThreads } from '../../utils/chatThreads';
import { formatInstant, isTodayInZone } from '../../utils/format';
import { uploadChatAttachment, attachmentProblem, fmtBytes, MAX_CHAT_FILES } from '../../utils/chatAttachments';

// ChatPanel — entity-scoped SHARED chat.
//
// A thread belongs to the deal (or deal detail) it hangs off, not to whoever
// started it: everyone with access to the record sees every thread here and may
// reply to it, with no sharing step. That is the thing the UI has to make
// obvious — hence the per-message author line (a thread genuinely has several
// speakers), the presence row (someone else may be reading it right now), and
// the explicit "Visible to everyone with access to this …" copy.
//
// Because it is shared, nothing is ever destroyed: an edited question or a
// regenerated answer appears as a REVISION of that turn, with the previous
// version still reachable underneath. Someone else may be part-way through
// reading the thing you just changed.
//
// Harvi can also PROPOSE a note or a task, and cannot write either: a propose_*
// tool records a draft, and what turns it into a row is the person reading the
// card pressing Save, which calls the same endpoint the app's own forms call
// with their own token. The cards are rendered under the answer that proposed
// them, and their status comes from the server — so a reloaded page shows a
// saved draft as saved rather than offering to save it twice.
//
// Answers can cite their sources: an assistant turn may carry mcp_tool_use /
// mcp_tool_result blocks from the DAM connector AND tool_use / tool_result
// blocks from the Harvi tools that read this database. Both render as one
// collapsed row that expands to show what was actually pulled. People
// need to see where a number came from before they act on it.
//
// ── THE LAYOUT IS A CHAT, NOT A FORM ────────────────────────────────────────
// It used to be a stack: a thread list and a transcript side by side in a
// wrapping flex row, with every control the thread has — two labelled selects,
// a visibility sentence, badges, an export button, a comparison bar — piled
// ABOVE the messages. On the floating widget that chrome was most of the panel
// and the conversation was a 320px letterbox underneath it. So:
//   * the panel is a fixed HEADER, a scrolling TRANSCRIPT and a pinned
//     COMPOSER — the transcript is the only thing that scrolls, and the
//     composer never leaves the screen;
//   * the settings that are read once and changed rarely (audience, model,
//     export) live in an overflow menu on the header, not in front of the
//     conversation. What must stay visible is what a person needs BEFORE they
//     type — who can read this — and that is one muted line;
//   * the thread list is a persistent rail on a wide container and an overlay
//     drawer on a narrow one, measured off THIS element rather than the
//     viewport, because the widget is 760px wide inside a 1600px window.
// THE AUDIENCE LINE MUST NEVER UNDERSTATE WHO CAN READ THIS — it is the one
// thing a person needs before they type. An auto deal tag GRANTS access to
// that deal's readers, so a thread tagged with deals it read has a wider
// audience than its own record implies, and saying only "everyone with access
// to this deal" would be a smaller number than the truth.
//
// A Private thread is unaffected: a tag files it for its author and nobody
// else, so the copy stays absolute.
export const visibilityCopy = (visibility, entityType, label, taggedCount = 0) => {
  if (visibility === 'Private') return 'Visible only to you';
  if (visibility === 'Org' || entityType === 'General') return 'Visible to every Harvus user';
  if (taggedCount > 0) {
    return `Visible to everyone with access to this ${label}, or to ${
      taggedCount === 1 ? 'the deal' : 'any of the deals'} tagged on it`;
  }
  return `Visible to everyone with access to this ${label}`;
};

const MODEL_LABELS = {
  'claude-sonnet-5': 'Sonnet',
  'claude-opus-5':   'Opus',
};
const MODEL_BLURBS = {
  'claude-sonnet-5': 'Everyday questions',
  'claude-opus-5':   'Underwriting & structuring · costs more',
};

// Long threads are paged rather than virtualized: render the tail, and reveal
// the rest on request.
const VISIBLE_TAIL = 40;

// A stable empty array, so a message with no proposals does not get a fresh
// reference every render and re-render MessageRow for nothing.
const EMPTY = [];

// A reply is indented, but only so far — past this the transcript stops being
// readable and the nesting stops meaning anything.
const MAX_INDENT = 3;

// Below this the thread rail becomes an overlay drawer. Measured on the PANEL,
// not the window: the floating widget is 760px wide wherever it is mounted.
const RAIL_MIN_WIDTH = 820;

// THE CONVERSATION IS A COLUMN, NOT THE WIDTH OF THE PANEL. Prose set across
// 1,100px is genuinely hard to read — the eye loses the line on the way back —
// which is why every assistant of this shape centres the transcript in a
// measure of roughly 70-80 characters and lets the chrome have the rest. The
// composer takes the SAME width, so a question and its answer sit on one axis
// instead of the input spanning a width nothing else uses.
const COLUMN = 740;

// Past this many chats the sidebar gets a search box. Below it, scanning is
// faster than typing, and an always-present empty field is one more thing
// between somebody and their conversation.
const SEARCH_AFTER = 8;

// AN EMPTY THREAD WITH A BLINKING CURSOR TELLS NOBODY WHAT HARVI CAN DO. These
// are the shapes of question the scoped tool layer actually answers well, so
// they double as documentation of the surface.
const STARTERS = {
  general: [
    'Which loans mature this year?',
    'What tasks are overdue on my deals?',
    'What did we say about Fairhope last month?',
  ],
  record: [
    'Summarise this record in five lines.',
    'What does the appraisal say about the cap rate?',
    'What are the biggest risks here?',
  ],
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  return isTodayInZone(iso)
    ? formatInstant(iso, { hour: 'numeric', minute: '2-digit' })
    : formatInstant(iso, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const initialsOf = (name) => String(name || '?')
  .split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('') || '?';

const textOf = (message) => message?.text
  || (Array.isArray(message?.content) ? message.content.filter(b => b.type === 'text').map(b => b.text).join('\n') : '');

// ── Small shared styles ─────────────────────────────────────────────────────
const subtleBtn = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
  fontFamily: 'inherit', fontSize: font.sm, color: B.accent, fontWeight: weight.medium,
};

const quietAction = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  background: 'none', border: '1px solid transparent', borderRadius: radius.pill,
  padding: '3px 9px', cursor: 'pointer', fontFamily: 'inherit',
  fontSize: font.xs, fontWeight: weight.medium, color: B.textMd,
  transition: 'background .15s, color .15s, border-color .15s',
};

const headerIconBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
  background: 'none', border: `1px solid ${B.divider}`, borderRadius: radius.lg,
  padding: '6px 9px', cursor: 'pointer', fontFamily: 'inherit',
  fontSize: font.sm, fontWeight: weight.medium, color: B.textMd,
  transition: 'background .15s, color .15s, border-color .15s',
};

const hoverSwap = (enter, leave) => ({
  onMouseEnter: (e) => Object.assign(e.currentTarget.style, enter),
  onMouseLeave: (e) => Object.assign(e.currentTarget.style, leave),
});

// ── The panel measures ITSELF ───────────────────────────────────────────────
// The same component renders full-width inside a deal page and 760px wide
// inside the floating widget, so a viewport media query would give the wrong
// answer to one of them every time.
const useContainerWidth = (ref) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof ResizeObserver === 'undefined') { setWidth(el.offsetWidth); return undefined; }
    const ro = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
};

// ── Popover ─────────────────────────────────────────────────────────────────
const Popover = ({ open, onClose, width = 288, placement = 'down', align = 'right', children }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey  = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      ref={ref}
      role="menu"
      style={{
        position: 'absolute', zIndex: 40, width,
        ...(placement === 'up' ? { bottom: 'calc(100% + 6px)' } : { top: 'calc(100% + 6px)' }),
        ...(align === 'left' ? { left: 0 } : { right: 0 }),
        background: B.surface, border: `1px solid ${B.divider}`, borderRadius: radius.xxxl,
        boxShadow: B.shadowXl, padding: space.sm, maxHeight: 320, overflowY: 'auto',
        animation: 'harvus-pop-in .16s cubic-bezier(0.22,1,0.36,1)',
      }}
    >{children}</div>
  );
};

const MenuLabel = ({ children }) => (
  <div style={{
    fontSize: font.xs, fontWeight: weight.semibold, color: B.textLt,
    letterSpacing: 0.5, textTransform: 'uppercase', padding: '8px 10px 4px',
  }}>{children}</div>
);

const MenuOption = ({ selected, title, blurb, onClick, disabled }) => (
  <button
    type="button" role="menuitemradio" aria-checked={!!selected}
    onClick={onClick} disabled={disabled}
    style={{
      display: 'flex', alignItems: 'flex-start', gap: space.sm, width: '100%', textAlign: 'left',
      background: selected ? B.accentLt : 'transparent', border: 'none', borderRadius: radius.xl,
      padding: '8px 10px', cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
      opacity: disabled ? 0.5 : 1, transition: 'background .12s',
    }}
    {...hoverSwap(
      { background: selected ? B.accentLt : B.hover },
      { background: selected ? B.accentLt : 'transparent' })}
  >
    <span style={{ color: selected ? B.accent : 'transparent', display: 'flex', marginTop: 1 }}>
      <Icon name="check" size={14} />
    </span>
    <span style={{ minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: font.base, color: B.text, fontWeight: selected ? weight.semibold : weight.regular }}>{title}</span>
      {blurb && <span style={{ display: 'block', fontSize: font.xs, color: B.textLt, marginTop: 1 }}>{blurb}</span>}
    </span>
  </button>
);

const MenuAction = ({ icon, children, onClick, disabled }) => (
  <button
    type="button" role="menuitem" onClick={onClick} disabled={disabled}
    style={{
      display: 'flex', alignItems: 'center', gap: space.sm, width: '100%', textAlign: 'left',
      background: 'transparent', border: 'none', borderRadius: radius.xl, padding: '9px 10px',
      cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
      fontSize: font.base, color: B.text, opacity: disabled ? 0.5 : 1,
    }}
    {...hoverSwap({ background: B.hover }, { background: 'transparent' })}
  >
    <span style={{ display: 'flex', color: B.textMd }}><Icon name={icon} size={15} /></span>
    {children}
  </button>
);

// ── Message body ────────────────────────────────────────────────────────────
// Structure the model actually emits, rendered as structure. See
// shared/src/chatFormat.ts for what is deliberately NOT parsed here (links —
// the web cites records through the `records` chips, never through prose).
const Runs = ({ runs }) => runs.map((run, i) => (
  run.code
    ? <code key={i} style={{
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.92em',
      background: B.surfaceSunken, borderRadius: radius.sm, padding: '1px 5px',
    }}>{run.text}</code>
    : run.bold
      ? <strong key={i} style={{ fontWeight: weight.semibold, color: B.text }}>{run.text}</strong>
      : run.italic
        ? <em key={i} style={{ fontStyle: 'italic' }}>{run.text}</em>
        : <span key={i}>{run.text}</span>
));

// ── A table inside an answer ────────────────────────────────────────────────
// THE WRAPPER SCROLLS; THE PANEL DOES NOT. This transcript lives at anything
// from a 380px phone sheet to a full deal tab, and a five-column filing plan
// does not fit the narrow end at a readable size. Letting the table set the
// page width would push the composer and every other message sideways with it,
// so the overflow is contained here — the one construct in a message allowed
// to be wider than the column it sits in. It is focusable because a region
// that scrolls has to be reachable without a pointer.
const MessageTable = ({ block, gap }) => (
  <div
    tabIndex={0}
    style={{
      marginTop: gap ? 10 : 0, overflowX: 'auto', maxWidth: '100%',
      border: `1px solid ${B.divider}`, borderRadius: radius.xl, background: B.surface,
    }}
  >
    <table style={{
      borderCollapse: 'collapse', width: '100%', fontSize: font.sm, lineHeight: 1.45,
      // THE TABLE OPTS OUT OF THE BODY'S `break-word`. Inherited, it drops every
      // column's minimum width to a single character, and the browser's column
      // algorithm then squeezes a narrow column to exactly that — rendering a
      // row number `10` stacked as "1" over "0". `break-word` still applies to
      // an unbroken token long enough to overflow on its own (a URL, a path),
      // so nothing escapes the panel; ordinary words simply stay whole.
      wordBreak: 'normal', overflowWrap: 'break-word',
    }}>
      <thead>
        <tr>
          {block.header.map((cell, c) => (
            <th key={c} style={{
              padding: '7px 11px', textAlign: block.align[c] || 'left', verticalAlign: 'top',
              fontWeight: weight.semibold, color: B.text, background: B.surfaceMuted,
              // A header is a word or two while its column may hold a whole
              // mail subject, so only the header refuses to wrap. Cells wrap,
              // which is what keeps a wordy column from making the horizontal
              // scroll endless.
              borderBottom: `1px solid ${B.divider}`, whiteSpace: 'nowrap',
            }}><Runs runs={cell} /></th>
          ))}
        </tr>
      </thead>
      <tbody>
        {block.rows.map((row, r) => (
          <tr key={r}>
            {row.map((cell, c) => (
              <td key={c} style={{
                padding: '7px 11px', textAlign: block.align[c] || 'left', verticalAlign: 'top',
                borderTop: r === 0 ? undefined : `1px solid ${B.divider}`,
              }}><Runs runs={cell} /></td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const MessageBody = ({ text, muted }) => {
  const blocks = useMemo(() => parseMessageBody(text), [text]);
  if (!blocks.length) return null;
  const color = muted ? B.textMd : B.text;
  return (
    <div style={{ fontSize: font.base, lineHeight: 1.62, color, wordBreak: 'break-word' }}>
      {blocks.map((block, i) => {
        const gap = i === 0 ? 0 : 8;
        if (block.type === 'table') return <MessageTable key={i} block={block} gap={gap} />;
        if (block.type === 'rule') {
          return (
            <hr key={i} style={{
              border: 0, borderTop: `1px solid ${B.divider}`, margin: '14px 0 0',
            }} />
          );
        }
        if (block.type === 'code') {
          return (
            <pre key={i} style={{
              margin: `${gap}px 0 0`, padding: '10px 12px', overflowX: 'auto',
              background: B.surfaceSunken, borderRadius: radius.xl, border: `1px solid ${B.divider}`,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: font.sm, lineHeight: 1.5, color: B.text,
            }}>{block.text}</pre>
          );
        }
        if (block.type === 'heading') {
          return (
            <div key={i} style={{
              marginTop: i === 0 ? 0 : 14, marginBottom: 2,
              fontSize: block.level <= 2 ? font.md : font.base,
              fontWeight: weight.semibold, color: B.text, letterSpacing: -0.1,
            }}><Runs runs={block.runs} /></div>
          );
        }
        if (block.type === 'bullet' || block.type === 'numbered') {
          return (
            <div key={i} style={{ display: 'flex', gap: 8, marginTop: gap ? 4 : 0 }}>
              <span style={{
                flexShrink: 0, color: B.tealDk, fontWeight: weight.semibold,
                minWidth: block.type === 'numbered' ? 18 : 8, textAlign: 'right',
              }}>{block.type === 'numbered' ? block.marker : '•'}</span>
              <span style={{ minWidth: 0 }}><Runs runs={block.runs} /></span>
            </div>
          );
        }
        return (
          <div key={i} style={{ marginTop: gap }}><Runs runs={block.runs} /></div>
        );
      })}
    </div>
  );
};

// ── Presence ────────────────────────────────────────────────────────────────
const PresenceRow = ({ presence }) => {
  const others = (presence || []).filter(p => !p.isSelf);
  if (!others.length) return null;
  const typing = others.filter(p => p.state === 'typing');
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
      padding: '6px 0 2px',
    }}>
      <span style={{ display: 'flex', paddingRight: 2 }}>
        {others.slice(0, 4).map((p, i) => (
          <span
            key={p.userId}
            title={`${p.name}${p.state === 'typing' ? ' is typing' : ' is here'}`}
            style={{
              width: 22, height: 22, borderRadius: '50%', fontSize: 9, fontWeight: weight.bold,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: p.state === 'typing' ? B.green : B.navyLt, color: B.white,
              border: `2px solid ${B.surface}`, marginLeft: i ? -7 : 0,
            }}
          >{initialsOf(p.name)}</span>
        ))}
      </span>
      <span style={{ fontSize: font.xs, color: B.textMd }}>
        {typing.length
          ? `${typing.map(p => p.name).join(', ')} ${typing.length === 1 ? 'is' : 'are'} typing…`
          : `${others.map(p => p.name).join(', ')} ${others.length === 1 ? 'is' : 'are'} here`}
      </span>
    </div>
  );
};

// ── Tool provenance ─────────────────────────────────────────────────────────
// Two kinds of call land here and both are provenance the reader needs:
//   mcp_tool_use   — the DAM connector, executed by Anthropic against the asset
//                    library.
//   tool_use       — the Harvi tools, executed inside the API against this
//                    database under the asking user's own scope.
// They share a shape (id / name / input, paired by tool_use_id), so one row
// renders both. The LABEL distinguishes them, because "Searched documents" over
// a run of list_deals calls would describe work that never happened.
const USE_TYPES    = ['mcp_tool_use', 'tool_use'];
const RESULT_TYPES = ['mcp_tool_result', 'tool_result'];

// A THIRD KIND rides on tool_use: research_web reaches the public internet,
// and "Checked Harvus" over a web search would tell the reader a figure came
// from our records when it came from a web page — the one confusion the tool
// exists to keep apart. So the web is named whenever it was reached.
// A FOURTH: code_execution is Python run in Anthropic's sandbox (the API
// rewrites its blocks into tool_use/tool_result for us). A calculation is not a
// lookup, and "Checked Harvus" over one would claim a record was read.
const CODE_TOOL = 'code_execution';
const traceLabel = (uses) => {
  const dam   = uses.some(u => u.type === 'mcp_tool_use');
  const web   = uses.some(u => u.type === 'tool_use' && u.name === 'research_web');
  const code  = uses.some(u => u.type === 'tool_use' && u.name === CODE_TOOL);
  const harvi = uses.some(u => u.type === 'tool_use' && u.name !== 'research_web' && u.name !== CODE_TOOL);
  const kinds = [dam, web, harvi, code].filter(Boolean).length;
  if (kinds > 1) {
    if (web) return 'Looked things up, including the web';
    return code ? 'Looked things up and ran calculations' : 'Looked things up';
  }
  if (code) return 'Ran calculations';
  if (web) return 'Searched the web';
  return dam ? 'Searched documents' : 'Checked Harvus';
};

const Disclosure = ({ open, onToggle, icon, label, count, tone = B.textMd }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: open ? B.surfaceMuted : 'transparent',
      border: `1px solid ${open ? B.divider : 'transparent'}`,
      borderRadius: radius.pill, padding: '3px 10px 3px 8px', cursor: 'pointer',
      fontFamily: 'inherit', fontSize: font.xs, fontWeight: weight.medium, color: tone,
      transition: 'background .15s, border-color .15s',
    }}
    {...hoverSwap(
      { background: B.surfaceMuted, borderColor: B.divider },
      { background: open ? B.surfaceMuted : 'transparent', borderColor: open ? B.divider : 'transparent' })}
  >
    {icon && <span style={{ display: 'flex', opacity: 0.8 }}><Icon name={icon} size={12} /></span>}
    {label}{count != null && <span style={{ opacity: 0.65 }}>· {count}</span>}
    <span style={{ display: 'flex', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
      <Icon name="chevronDown" size={12} />
    </span>
  </button>
);

// A call the turn's lookup budget refused (or the output limit cut off) never
// reached a tool, so it is "not run", not "failed" — the same rule as
// notRunReason in api/src/conversations.js, which labels the PDF export.
const notRunOf = (text) => {
  try {
    const p = JSON.parse(text || '');
    if (p && (p.notRun === 'budget' || p.notRun === 'cutoff')) return p.notRun;
    if (/^Lookup budget spent/.test(String(p?.error || ''))) return 'budget';
    if (/^This call was cut off at the output limit/.test(String(p?.error || ''))) return 'cutoff';
  } catch { /* prose result */ }
  return null;
};

const ToolTrace = ({ blocks }) => {
  const [open, setOpen] = useState(false);
  const uses = blocks.filter(b => USE_TYPES.includes(b.type));
  if (!uses.length) return null;
  const results = blocks.filter(b => RESULT_TYPES.includes(b.type));
  const resultFor = (id) => results.find(r => r.tool_use_id === id);

  return (
    <div style={{ marginTop: 10 }}>
      <Disclosure open={open} onToggle={() => setOpen(o => !o)} icon="search" label={traceLabel(uses)} count={uses.length} />
      {open && (
        <div style={{
          marginTop: 8, padding: space.md, display: 'flex', flexDirection: 'column', gap: space.md,
          background: B.surfaceMuted, border: `1px solid ${B.divider}`, borderRadius: radius.xxl,
        }}>
          {uses.map((use, i) => {
            const result = resultFor(use.id);
            const resultText = (Array.isArray(result?.content) ? result.content : [])
              .filter(c => c && c.type === 'text').map(c => c.text).join('\n');
            return (
              <div key={use.id || i} style={{ fontSize: font.sm, color: B.textMd, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <code style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: font.xs, fontWeight: weight.semibold, color: B.navy,
                    background: B.surface, border: `1px solid ${B.divider}`,
                    borderRadius: radius.md, padding: '2px 6px',
                  }}>{use.name}</code>
                  {result?.is_error && (notRunOf(resultText)
                    ? <Badge color={B.textLt}>not run — {notRunOf(resultText) === 'budget' ? 'lookup budget reached' : 'cut off'}</Badge>
                    : <Badge color={B.red}>failed</Badge>)}
                </div>
                {use.input && (
                  // Code is shown as code, not as a JSON-escaped string.
                  <pre style={{
                    margin: '5px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    background: B.surface, border: `1px solid ${B.divider}`,
                    padding: '6px 8px', borderRadius: radius.lg, fontSize: font.xs, color: B.textMd,
                  }}>{use.name === CODE_TOOL && typeof use.input.code === 'string' ? use.input.code : JSON.stringify(use.input)}</pre>
                )}
                {resultText && (
                  <div style={{
                    marginTop: 5, whiteSpace: 'pre-wrap', maxHeight: 170, overflowY: 'auto',
                    fontSize: font.xs, lineHeight: 1.5, color: B.textMd,
                    borderLeft: `2px solid ${B.border}`, paddingLeft: 8,
                  }}>{resultText.slice(0, 2000)}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Superseded turns ────────────────────────────────────────────────────────
// The old version is kept and shown on request — someone else may have read it,
// and a transcript that silently rewrites itself is worse than one that shows
// its own history.
const VersionTrace = ({ versions, role }) => {
  const [open, setOpen] = useState(false);
  if (!versions?.length) return null;
  const label = role === 'user'
    ? `Edited · ${versions.length} earlier version${versions.length === 1 ? '' : 's'}`
    : `Regenerated · ${versions.length} earlier answer${versions.length === 1 ? '' : 's'}`;
  return (
    <div style={{ marginTop: 8 }}>
      <Disclosure open={open} onToggle={() => setOpen(o => !o)} icon="undo" label={label} tone={B.textLt} />
      {open && versions.map((v) => (
        <div key={v.id} style={{
          marginTop: 6, padding: '6px 0 6px 10px', borderLeft: `2px solid ${B.border}`,
          fontSize: font.sm, color: B.textLt, whiteSpace: 'pre-wrap',
        }}>
          <div style={{ fontSize: font.xs, marginBottom: 2 }}>{fmtWhen(v.createdAt)}</div>
          {v.text}
        </div>
      ))}
    </div>
  );
};

// ── Record links ────────────────────────────────────────────────────────────
// The records this answer actually read, as chips under it. The list is the
// SERVER's (`message.records`, derived from the tool calls) — the client does
// not go looking through prose for ids, and a link therefore can never point
// somewhere the model merely mentioned.
//
// NAVIGATION GOES THROUGH THE `harvi-navigate` EVENT the app already listens
// for, not through a prop threaded down from every mount. ChatPanel is rendered
// inside a deal page and inside the floating widget, and neither is a place to
// know how the app routes; Contacts.jsx dispatches the same event for the same
// reason.
//
// A NOTE HAS NO ROUTE OF ITS OWN and opens its parent carrying its id, which is
// why the server sends the parents alongside it. That is not a shortcoming to
// paper over — a note is read in the feed it belongs to.
const RECORD_LABELS = {
  deal: 'Deal', dealDetail: 'Capital item', task: 'Task', document: 'Document', note: 'Note',
};
const RECORD_ICONS = {
  deal: 'deals', dealDetail: 'ledger', task: 'tasks', document: 'file', note: 'note',
};

// A DOCUMENT OPENS THE FILE, not the Documents page. Sending someone to a page
// listing thousands of files, to find the one the answer just quoted, is a link
// that does not do its job. The SAS is universal, so one round trip opens
// anything — and the tab is opened SYNCHRONOUSLY inside the click, before the
// await, or the popup blocker kills it (the openDocument trick).
const openDocumentRecord = async (rec, onError) => {
  const win = window.open('', '_blank');
  try {
    const { sasUrl } = await api.get(`documents/${rec.id}/download-url`);
    if (!sasUrl) throw new Error('No link came back for that file.');
    if (win) win.location.href = sasUrl; else window.open(sasUrl, '_blank');
  } catch (e) {
    if (win) win.close();
    onError?.(`Could not open ${rec.label || 'that document'}: ${e.message}`);
  }
};

const navigateToRecord = (rec, onError) => {
  if (!rec || !rec.id) return;
  if (rec.type === 'document') { void openDocumentRecord(rec, onError); return; }
  const detail = {
    deal:       { view: 'deals', deal: rec.id },
    dealDetail: { view: 'deals', deal: rec.dealId, detail: rec.id },
    task:       { view: 'tasks', task: rec.id },
    note:       rec.taskId
      ? { view: 'tasks', task: rec.taskId, note: rec.id }
      : { view: 'deals', deal: rec.dealId, detail: rec.dealDetailId, note: rec.id },
  }[rec.type];
  if (!detail) return;
  window.dispatchEvent(new CustomEvent('harvi-navigate', { detail }));
};

const RecordLinks = ({ records }) => {
  const [err, setErr] = useState(null);
  const list = Array.isArray(records) ? records.filter(r => r && r.id && RECORD_LABELS[r.type]) : [];
  if (!list.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
      {list.map((rec) => (
        <button
          key={`${rec.type}:${rec.id}`}
          type="button"
          onClick={() => navigateToRecord(rec, setErr)}
          title={`Open this ${RECORD_LABELS[rec.type].toLowerCase()}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 280,
            padding: '4px 11px 4px 8px', borderRadius: radius.pill, cursor: 'pointer',
            border: `1px solid ${B.divider}`, background: B.surface, boxShadow: B.shadowXs,
            fontFamily: 'inherit', fontSize: font.xs, fontWeight: weight.medium, color: B.navy,
            transition: 'background .15s, border-color .15s, box-shadow .15s',
          }}
          {...hoverSwap(
            { background: B.accentLt, borderColor: B.accent + '55', boxShadow: 'none' },
            { background: B.surface, borderColor: B.divider, boxShadow: B.shadowXs })}
        >
          <span style={{ display: 'flex', color: B.tealDk, flexShrink: 0 }}>
            <Icon name={RECORD_ICONS[rec.type] || 'file'} size={13} />
          </span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {rec.label || RECORD_LABELS[rec.type]}
          </span>
        </button>
      ))}
      {err && <div style={{ flexBasis: '100%', fontSize: font.xs, color: B.red }}>{err}</div>}
    </div>
  );
};

// ── Harvi's own avatar ──────────────────────────────────────────────────────
const HarviMark = ({ size = 30 }) => (
  <span
    aria-hidden="true"
    style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: B.navy, backgroundImage: B.gradBrand, color: B.teal,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: B.shadowXs,
    }}
  ><Icon name="sparkles" size={Math.round(size * 0.5)} /></span>
);

const ThinkingDots = ({ label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0' }}>
    <HarviMark />
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: 6, height: 6, borderRadius: '50%', background: B.tealDk,
          animation: 'harvus-dot 1.2s ease-in-out infinite', animationDelay: `${i * 0.16}s`,
        }} />
      ))}
    </span>
    {label && <span style={{ fontSize: font.sm, color: B.textLt }}>{label}</span>}
  </div>
);

const Caret = () => (
  <span style={{
    display: 'inline-block', width: 2, height: '1em', background: B.tealDk,
    marginLeft: 2, verticalAlign: 'text-bottom', animation: 'harvus-blink 1s step-end infinite',
  }} />
);

// ── Files on a message ──────────────────────────────────────────────────────
// Names only — the bytes come through the download route, authorized like the
// thread. A file that was also filed on the deal says so, because "is this on
// Documents or only in the chat?" is the question somebody asks next.
const AttachmentChips = ({ message, onOpen }) => {
  const list = Array.isArray(message.attachments) ? message.attachments : [];
  if (!list.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
      {list.map((a) => (
        <button
          key={`${a.index}:${a.fileName}`}
          type="button"
          onClick={() => onOpen && !message.optimistic && onOpen(message, a)}
          title={a.savedToDocuments ? 'Also saved on this deal\'s Documents tab' : 'Attached to this chat only'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 280,
            padding: '5px 10px', borderRadius: radius.pill, cursor: onOpen ? 'pointer' : 'default',
            border: `1px solid ${B.divider}`, background: B.surface, fontFamily: 'inherit',
            fontSize: font.xs, color: B.text,
          }}
        >
          <Icon name="attach" size={12} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.fileName}</span>
          {a.sizeBytes ? <span style={{ color: B.textLt, flexShrink: 0 }}>{fmtBytes(a.sizeBytes)}</span> : null}
          {a.savedToDocuments && <span style={{ color: B.tealDk, flexShrink: 0 }}>· Documents</span>}
        </button>
      ))}
    </div>
  );
};

// ── One message ─────────────────────────────────────────────────────────────
// A QUESTION IS A BUBBLE AND AN ANSWER IS NOT, and neither is right-aligned.
// ── Rating an answer ────────────────────────────────────────────────────────
// Thumbs up / down on one of Harvi's answers, per person (a shared thread is
// rated by each reader separately, and only YOUR rating is ever shown to you).
// Clicking the thumb you already chose clears it. A thumbs-down offers an
// optional line on what was wrong — that line is what turns a rating into an
// eval case worth keeping, so it is asked for but never required.
export const FeedbackControls = ({ message, conversationId, onChanged, visible }) => {
  const mine = message.myFeedback || null;
  const [saving, setSaving] = useState(false);
  const [askWhy, setAskWhy] = useState(false);
  const [why, setWhy] = useState('');
  const [err, setErr] = useState(null);
  const path = `conversations/${conversationId}/messages/${message.id}/feedback`;

  const rate = async (rating, comment = null) => {
    setSaving(true); setErr(null);
    try {
      if (mine && mine.rating === rating && comment === null) {
        await api.del(path);
        onChanged(message.id, null);
        setAskWhy(false);
      } else {
        const res = await api.put(path, { rating, comment });
        onChanged(message.id, res.myFeedback || { rating, comment });
        setAskWhy(rating === 'down' && comment === null);
        if (comment !== null) setWhy('');
      }
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const thumb = (rating, icon, label) => {
    const on = mine?.rating === rating;
    return (
      <button
        type="button" aria-pressed={on} aria-label={label} title={on ? `${label} (click to clear)` : label}
        disabled={saving} onClick={() => rate(rating)}
        style={{ ...quietAction, padding: '3px 6px', color: on ? B.navy : B.textMd,
                 background: on ? B.surfaceMuted : 'none', borderColor: on ? B.divider : 'transparent' }}
      >
        <Icon name={icon} size={12} />
      </button>
    );
  };

  // Rendered even when hidden, so a keyboard user can tab to it (the row's
  // onFocus reveals it); only opacity changes.
  const shown = visible || !!mine || askWhy;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                   opacity: shown ? 1 : 0, transition: 'opacity .15s' }}>
      {thumb('up', 'thumbsUp', 'Good answer')}
      {thumb('down', 'thumbsDown', 'Bad answer')}
      {askWhy && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 6 }}>
          <input
            value={why} onChange={(e) => setWhy(e.target.value)} maxLength={2000}
            placeholder="What was wrong? (optional)"
            onKeyDown={(e) => { if (e.key === 'Enter' && why.trim()) rate('down', why.trim()); }}
            style={{ fontSize: font.xs, padding: '4px 8px', border: `1px solid ${B.divider}`,
                     borderRadius: radius.lg, minWidth: 220, fontFamily: 'inherit' }}
          />
          <button type="button" disabled={saving || !why.trim()} onClick={() => rate('down', why.trim())} style={quietAction}>Send</button>
          <button type="button" onClick={() => setAskWhy(false)} style={quietAction}>Skip</button>
        </span>
      )}
      {mine?.comment && !askWhy && (
        <span style={{ fontSize: font.xs, color: B.textLt, marginLeft: 4 }}>“{mine.comment}”</span>
      )}
      {err && <span style={{ fontSize: font.xs, color: B.red, marginLeft: 6 }}>{err}</span>}
    </span>
  );
};

// The thread is genuinely multi-speaker — three people and Harvi in one
// transcript — so "mine right, theirs left" has nothing to say about a
// colleague's question. Everything runs down one column with an author; the
// bubble marks the prompt, and the answer sits open on the page where a long
// one is easiest to read.
const MessageRow = ({
  message, depth = 0, pending, busy, grouped = false,
  onReply, onEdit, onRegenerate, features,
  proposals = [], assignees = [], onProposalChanged, onOpenAttachment,
  conversationId, onFeedback, draftTarget = null,
}) => {
  const [hover, setHover] = useState(false);
  const isUser = message.role === 'user';
  const author = isUser ? (message.createdByName || 'Someone') : 'Harvi';
  const blocks = Array.isArray(message.content) ? message.content : [];
  const body = textOf(message);
  const canReply      = features?.threads && !message.optimistic;
  const canEdit       = features?.threads && isUser && message.isMine && message.isTail && !message.optimistic;
  const canRegenerate = features?.threads && !isUser && message.isTail;
  const canRate       = features?.feedback && !isUser && !message.optimistic && !pending && !!onFeedback;
  const anyAction = canReply || canEdit || canRegenerate || canRate;

  const action = (icon, label, onClick) => (
    <button
      type="button" onClick={() => onClick(message)} disabled={busy} style={quietAction}
      {...hoverSwap(
        { background: B.surfaceMuted, color: B.text, borderColor: B.divider },
        { background: 'none', color: B.textMd, borderColor: 'transparent' })}
    >
      <Icon name={icon} size={12} />{label}
    </button>
  );

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', gap: 12,
        padding: grouped ? '4px 0 0' : `${space.lg}px 0 0`,
        marginLeft: Math.min(depth, MAX_INDENT) * 20,
        borderLeft: depth > 0 ? `2px solid ${B.divider}` : undefined,
        paddingLeft: depth > 0 ? 12 : 0,
        animation: message.optimistic || pending ? 'harvus-msg-in .2s ease-out' : undefined,
      }}
    >
      <div style={{ width: 30, flexShrink: 0 }}>
        {!grouped && (isUser
          ? <Avatar initials={initialsOf(author)} size={30} color={B.tealDk} />
          : <HarviMark />)}
      </div>

      <div style={{ minWidth: 0, flex: 1 }}>
        {!grouped && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontWeight: weight.semibold, fontSize: font.base, color: B.text }}>{author}</span>
            <span style={{ fontSize: font.xs, color: B.textLt }}>{fmtWhen(message.createdAt)}</span>
            {!isUser && message.model && (
              <span style={{ fontSize: font.xs, color: B.textLt }}>· {MODEL_LABELS[message.model] || message.model}</span>
            )}
            {depth > 0 && <span style={{ fontSize: font.xs, color: B.textLt }}>· reply</span>}
          </div>
        )}

        <AttachmentChips message={message} onOpen={onOpenAttachment} />
        {isUser ? (body || !(message.attachments || []).length) && (
          <div style={{
            display: 'inline-block', maxWidth: '100%',
            background: B.surfaceMuted, border: `1px solid ${B.divider}`,
            borderRadius: radius.xxxl, padding: '9px 14px',
          }}>
            <MessageBody text={body} />
          </div>
        ) : (
          <div>
            <MessageBody text={body} />
            {pending && <Caret />}
          </div>
        )}

        {/* toolBlocks is the server's normalized list (code execution included);
            content is the fallback for a message built client-side mid-turn. */}
        {!isUser && <ToolTrace blocks={Array.isArray(message.toolBlocks) ? message.toolBlocks : blocks} />}
        {!isUser && <RecordLinks records={message.records} />}
        {/* Cards for anything this turn proposed. They live here rather than at
            the foot of the thread so the proposal sits beside the sentence that
            announced it. */}
        {proposals.map((d) => (
          <AssistantDraftCard key={d.draftId} draft={d} assignees={assignees} onChanged={onProposalChanged} defaultTarget={draftTarget} />
        ))}
        <VersionTrace versions={message.versions} role={message.role} />

        {/* Row actions fade in on hover but stay real, focusable buttons, so a
            keyboard user reaches them without a pointer ever being here. */}
        {anyAction && (
          <div style={{
            display: 'flex', gap: 4, marginTop: 6, marginLeft: -9, alignItems: 'center', flexWrap: 'wrap',
          }}
            onFocus={() => setHover(true)}
          >
            {/* The row actions fade in on hover (still focusable); a rating the
                reader has already given stays visible without it. */}
            <span style={{ display: 'inline-flex', gap: 4, opacity: hover ? 1 : 0, transition: 'opacity .15s' }}>
              {canReply && action('message', 'Reply', onReply)}
              {canEdit && action('edit', 'Edit', onEdit)}
              {canRegenerate && action('refresh', 'Regenerate', onRegenerate)}
            </span>
            {canRate && (
              <FeedbackControls
                message={message} conversationId={conversationId}
                onChanged={onFeedback} visible={hover}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Thread sidebar ──────────────────────────────────────────────────────────
// Shaped like the sidebar of any assistant people already use: a "New chat"
// action pinned at the top, then history grouped by recency under quiet
// headings. The row carries the TITLE and nothing else — the date is the
// group's job, and repeating "Neil Godfrey" down a private surface says
// nothing, so the author is dropped from the row entirely.
const ThreadList = ({ threads, activeId, onSelect, onNew, loading, general }) => {
  const [query, setQuery] = useState('');
  const shown = useMemo(() => filterThreads(threads, query), [threads, query]);
  const groups = useMemo(() => groupThreadsByDate(shown), [shown]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%' }}>
      <div style={{ padding: `${space.md}px ${space.md}px ${space.sm}px`, flexShrink: 0 }}>
        <button
          type="button" onClick={onNew}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%',
            background: B.surface, border: `1px solid ${B.divider}`, borderRadius: radius.xl,
            padding: '9px 11px', cursor: 'pointer', fontFamily: 'inherit',
            fontSize: font.base, fontWeight: weight.semibold, color: B.navy,
            boxShadow: B.shadowXs, transition: 'background .15s, border-color .15s',
          }}
          {...hoverSwap(
            { background: B.accentLt, borderColor: `${B.accent}55` },
            { background: B.surface, borderColor: B.divider })}
        >
          <Icon name="edit" size={14} /> New chat
        </button>

        {threads.length > SEARCH_AFTER && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 7, marginTop: space.sm,
            padding: '6px 10px', borderRadius: radius.xl,
            background: B.surface, border: `1px solid ${B.divider}`,
          }}>
            <span style={{ display: 'flex', color: B.textLt }}><Icon name="search" size={13} /></span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search chats"
              aria-label="Search chats"
              style={{
                flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent',
                fontFamily: 'inherit', fontSize: font.sm, color: B.text,
              }}
            />
          </div>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: `0 ${space.sm}px ${space.md}px` }}>
        {loading && (
          <div style={{ fontSize: font.sm, color: B.textLt, padding: '10px 8px' }}>Loading chats…</div>
        )}
        {!loading && !threads.length && (
          <div style={{ fontSize: font.sm, color: B.textLt, padding: '10px 8px', lineHeight: 1.5 }}>
            {general
              ? 'No chats yet. Start one — it stays private to you.'
              : 'No chats yet. Start one — everyone on this record will see it.'}
          </div>
        )}
        {!loading && threads.length > 0 && !shown.length && (
          <div style={{ fontSize: font.sm, color: B.textLt, padding: '10px 8px' }}>
            Nothing matches that.
          </div>
        )}

        {groups.map((group) => (
          <div key={group.label} style={{ marginTop: space.sm }}>
            <div style={{
              fontSize: font.xs, fontWeight: weight.semibold, color: B.textLt,
              letterSpacing: 0.3, padding: '6px 8px 4px',
            }}>{group.label}</div>
            {group.threads.map((t) => {
              const active = t.id === activeId;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onSelect(t.id)}
                  aria-current={active || undefined}
                  title={t.title || 'Untitled chat'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left',
                    border: 'none', borderRadius: radius.lg,
                    background: active ? B.accentLt : 'transparent',
                    padding: '7px 8px', cursor: 'pointer', fontFamily: 'inherit',
                    transition: 'background .12s',
                  }}
                  {...hoverSwap(
                    { background: active ? B.accentLt : B.hover },
                    { background: active ? B.accentLt : 'transparent' })}
                >
                  {t.visibility === 'Private' && (
                    <span style={{ display: 'flex', color: B.textLt, flexShrink: 0 }}><Icon name="lock" size={11} /></span>
                  )}
                  <span style={{
                    flex: 1, minWidth: 0, fontSize: font.base,
                    fontWeight: active ? weight.semibold : weight.regular,
                    color: active ? B.navyDk : B.text,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{t.title || 'Untitled chat'}</span>
                  {t.linkedCount > 0 && (
                    <span style={{ flexShrink: 0, fontSize: font.xs, color: B.textLt }}>+{t.linkedCount}</span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Records this thread is on ───────────────────────────────────────────────
// TWO ROWS, because the two kinds of link are different claims and a reader
// acts on them differently:
//
//   TAGGED (source 'auto') — deals Harvi read while answering, filed here so
//   the conversation shows up on the deal and its capital items without
//   anybody adding it. Removable, and a removal is PERMANENT: the server keeps
//   the soft-deleted row precisely so the next turn cannot put it back.
//
//   COMPARING (source 'user') — records somebody chose. These go into the
//   prompt, so they are capped at five, and reading the thread requires access
//   to every one of them.
//
// A picked record you cannot see comes back as a plain refusal rather than
// silently doing nothing.
const ComparisonBar = ({ entities, onChange, disabled }) => {
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState('');
  const { dealPickerItems } = useDealPickerItems();

  const links = (entities || []).filter(e => !e.primary);
  const tagged    = links.filter(l => l.source === 'auto');
  const comparing = links.filter(l => l.source !== 'auto');

  // Removing a chip PUTs the rest — of both kinds. The server matches on the
  // record, so a tag that survives the round trip keeps being a tag; it does
  // not get promoted into the prompt by a click on a different chip.
  const removeRef = (gone) => onChange(
    links.filter(l => !(l.entityType === gone.entityType && l.entityId === gone.entityId))
         .map(l => ({ entityType: l.entityType, entityId: l.entityId })));

  const add = () => {
    const item = dealPickerItems.find(i => i.value === picked);
    if (!item) return;
    const ref = item.dealDetailID
      ? { entityType: 'DealDetail', entityId: item.dealDetailID }
      : { entityType: 'Deal',       entityId: item.dealID };
    onChange([...links.map(l => ({ entityType: l.entityType, entityId: l.entityId })), ref]);
    setPicked('');
    setAdding(false);
  };

  if (!links.length && !adding) {
    return (
      // alignSelf, because the notices column stretches its children and a
      // text link the full width of the panel does not read as a link.
      <button
        type="button" onClick={() => setAdding(true)} disabled={disabled}
        style={{ ...subtleBtn, fontSize: font.xs, alignSelf: 'flex-start' }}
      >
        + Compare with another record
      </button>
    );
  }

  return (
    <div>
      {tagged.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          <span style={{ fontSize: font.xs, color: B.textMd }}>
            On {tagged.length === 1 ? 'this deal' : 'these deals'}:
          </span>
          {tagged.map((l) => (
            <Chip
              key={`${l.entityType}:${l.entityId}`}
              color={B.green}
              onRemove={disabled ? undefined : () => removeRef(l)}
            >{l.label || l.entityId}</Chip>
          ))}
          <span style={{ fontSize: font.xs, color: B.textLt }}>
            · added by Harvi from what it read — remove any that aren&apos;t relevant
          </span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {comparing.length > 0 && <span style={{ fontSize: font.xs, color: B.textMd }}>Also comparing:</span>}
        {comparing.map((l) => (
          <Chip
            key={`${l.entityType}:${l.entityId}`}
            color={B.navy}
            onRemove={disabled ? undefined : () => removeRef(l)}
          >{l.label || l.entityId}</Chip>
        ))}
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} disabled={disabled} style={{ ...subtleBtn, fontSize: font.xs }}>+ add a record</button>
        )}
      </div>
      {adding && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 8, maxWidth: 520 }}>
          <div style={{ flex: 1 }}>
            <InlineSearchPicker
              label="Record to compare"
              items={dealPickerItems}
              value={picked}
              onChange={setPicked}
              placeholder="Search deals and capital positions…"
            />
          </div>
          <Btn size="sm" onClick={add} disabled={!picked}>Add</Btn>
          <Btn size="sm" variant="ghost" onClick={() => { setAdding(false); setPicked(''); }}>Cancel</Btn>
        </div>
      )}
    </div>
  );
};

// ── Callout ─────────────────────────────────────────────────────────────────
const Callout = ({ icon, tone = B.navy, tint = B.accentLt, children, actions }) => (
  <div style={{
    display: 'flex', gap: 10, alignItems: 'flex-start',
    padding: '10px 12px', borderRadius: radius.xxl,
    background: tint, border: `1px solid ${tone}22`,
  }}>
    <span style={{ display: 'flex', color: tone, marginTop: 1, flexShrink: 0 }}><Icon name={icon} size={15} /></span>
    <div style={{ flex: 1, minWidth: 0, fontSize: font.sm, color: B.text, lineHeight: 1.5 }}>
      {children}
      {actions && <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  </div>
);

// ── Panel ───────────────────────────────────────────────────────────────────
// THE 'General' SURFACE IS THE SAME PANEL, NOT A SECOND ONE. The floating
// widget in App.jsx used to be a separate chat entirely — it posted to the
// legacy bot endpoint, so the always-available Harvi on the web had no
// threads, no draft cards and no provenance, while the good one existed only
// inside a deal. Rather than port those features into a second implementation
// (which is how the two drifted in the first place), the widget mounts THIS
// panel with `general`. iOS made the same move in mobile/app/(tabs)/harvi.tsx.
//
// A General thread carries NO entityId — that is the API's rule
// (C.entityRefError), not a convenience here, so the id is null rather than a
// placeholder and every request is built from these three values.
export const ChatPanel = ({ dealId, dealDetailId, general = false, bodyMaxHeight = 520, paused = false, fill = false, openConversation = null, askRequest = null }) => {
  // A question typed into the header's Ask Harvi box: `{ text, n }`. It is
  // sent as the first turn of a NEW thread (General threads are private by
  // default); the nonce lets the same question be asked twice.
  const askingRef = useRef(null);
  const entityType = general ? 'General' : dealDetailId ? 'DealDetail' : 'Deal';
  const entityId   = general ? null : (dealDetailId || dealId);
  // Where a drafted file is offered to be saved by default: this thread's deal.
  const draftTarget = useMemo(() => (general ? null : { dealId: dealId || null, dealDetailId: dealDetailId || null }),
    [general, dealId, dealDetailId]);
  const entityLabel = general ? 'workspace' : dealDetailId ? 'capital position' : 'deal';

  const [threads, setThreads]   = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread]     = useState(null);      // the whole GET/live payload
  const [threadLoading, setThreadLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [confirmPromote, setConfirmPromote] = useState(false);
  const [confirmDelete, setConfirmDelete]   = useState(false);
  const [deleting, setDeleting]             = useState(false);
  const [draft, setDraft]       = useState('');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState(null);
  const [notice, setNotice]     = useState(null);
  const [showAll, setShowAll]   = useState(false);
  const [listOpen, setListOpen] = useState(false);     // narrow-container drawer
  const [railHidden, setRailHidden] = useState(false); // wide-container collapse
  const [menuOpen, setMenuOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [replyTo, setReplyTo]   = useState(null);      // message being replied to
  const [editing, setEditing]   = useState(null);      // message being edited
  const [pendingText, setPendingText] = useState(null);
  // Files waiting to go with the next message, and the person's answer to
  // "also add them to this deal's Documents?" — null until they answer. A
  // General thread is on no deal, so the question is never asked there.
  const [files, setFiles]       = useState([]);         // [{ key, file }]
  const [saveToDocs, setSaveToDocs] = useState(null);
  const [uploading, setUploading] = useState(null);     // "2 of 3" while files upload
  const fileInputRef = useRef(null);
  const [streamText, setStreamText] = useState(null);   // our own answer, arriving live
  const [exporting, setExporting] = useState(false);
  const [composerFocus, setComposerFocus] = useState(false);
  // Harvi's proposals for this thread. Named `proposals`, not `drafts` — the
  // composer's own text is already `draft` in this component.
  const [proposals, setProposals] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const rootRef     = useRef(null);
  const scrollRef   = useRef(null);
  const textareaRef = useRef(null);
  const stickRef    = useRef(true);
  const pollRef     = useRef(null);
  const draftRef    = useRef('');
  // While our own turn is streaming we render it from the SSE deltas, so the
  // /live poll must not ALSO render the same answer from the run row.
  const streamingRef = useRef(false);

  draftRef.current = draft;

  const width = useContainerWidth(rootRef);
  // 0 is "not measured yet" (the first paint, or no ResizeObserver): assume the
  // roomy layout rather than flashing the drawer on every mount.
  const roomForRail = width === 0 ? !general : width >= RAIL_MIN_WIDTH;
  // On a phone the header had a truncated title over a three-line subtitle
  // beside two badges — four competing things in 390px. Compact keeps the
  // title and the ONE fact that has to be visible before you type (who can
  // read this), on one line each; the rest is in the menu, where it already is.
  const compact = width > 0 && width < 560;
  const showRail = roomForRail && !railHidden;

  const loadThreads = useCallback(async (selectFirst = false) => {
    // General is the one type with no id, so "no id yet" is only a reason to
    // wait when the surface is supposed to have one.
    if (!general && !entityId) return;
    setListLoading(true);
    try {
      const qs = general ? 'entityType=General' : `entityType=${entityType}&entityId=${entityId}`;
      const res = await api.get(`conversations?${qs}`);
      const list = res?.conversations || [];
      setThreads(list);
      // Functional: a thread a notification opened must not be overwritten by
      // this first-load default, whose closure still sees activeId as null.
      if (selectFirst && list.length) setActiveId(cur => cur || (askingRef.current ? null : list[0].id));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setListLoading(false);
    }
  // activeId deliberately omitted — reloading the list must not reselect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, general]);

  useEffect(() => { loadThreads(true); }, [loadThreads]);

  // A notification opening ONE thread (the morning brief). `openConversation`
  // is `{ id, n }` — the nonce lets the same thread be opened twice. The list
  // is reloaded too, since the brief is a thread created after it was fetched.
  useEffect(() => {
    if (!openConversation?.id) return;
    setActiveId(openConversation.id);
    setListOpen(false);
    loadThreads(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openConversation?.id, openConversation?.n]);

  // Proposals are fetched BESIDE the thread rather than read out of the
  // persisted tool_result blocks: a tool result is frozen text and a draft's
  // status is not. Best-effort — a pre-173 database answers available:false and
  // the thread renders exactly as it did before this shipped.
  const loadProposals = useCallback(async () => {
    if (!activeId) { setProposals([]); return; }
    try {
      const res = await api.get(`assistant/drafts?conversationId=${activeId}`);
      setProposals(res?.available ? (res.drafts || []) : []);
    } catch {
      setProposals([]);
    }
  }, [activeId]);

  useEffect(() => { loadProposals(); }, [loadProposals]);

  // The assignee picker on a task card needs the user list, which is an
  // Admin-only endpoint — and propose_task is Admin-only for the same reason,
  // so this is only ever fetched by someone who can read it. Lazily, and only
  // when a task card is actually on screen.
  useEffect(() => {
    if (assignees.length) return;
    if (!proposals.some(d => d.kind === 'task' && d.status === 'open')) return;
    api.get('users?isActive=true')
      // Array.isArray, not `res?.users || res` — that fallback let an object
      // with no `users` key through as the list, and the draft card maps over
      // it, so one odd response took down the whole transcript.
      .then((res) => setAssignees(
        Array.isArray(res?.users) ? res.users : Array.isArray(res) ? res : []))
      .catch(() => setAssignees([]));
  }, [proposals, assignees.length]);

  useEffect(() => {
    let cancelled = false;
    if (!activeId) { setThread(null); return undefined; }
    setThreadLoading(true);
    api.get(`conversations/${activeId}`)
      .then((res) => { if (!cancelled) { setThread(res); setShowAll(false); setReplyTo(null); setEditing(null); setError(null); setConfirmPromote(false); setConfirmDelete(false); } })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setThreadLoading(false); });
    return () => { cancelled = true; };
  }, [activeId]);

  // ── Live loop ─────────────────────────────────────────────────────────────
  // One request does both jobs: it heartbeats this viewer's presence AND returns
  // the current transcript, the people here, and any in-flight answer. Presence
  // only works if polling IS the heartbeat, and the server picks the cadence —
  // fast while something is happening, slow while nothing is.
  //
  // `paused` EXISTS BECAUSE THE WIDGET STAYS MOUNTED WHILE CLOSED. Without this
  // gate every user who opened the bubble once went on heartbeating every 20s
  // on every page for the rest of the session, reporting themselves as present
  // in a thread nobody is looking at. Nothing is lost by stopping: our OWN
  // answer is awaited in `send`, and what this poll carries is somebody ELSE's
  // run and who else is here — both re-read on the next open.
  useEffect(() => {
    if (!activeId || paused) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const res = await api.post(`conversations/${activeId}/live`, {
          state: draftRef.current.trim() ? 'typing' : 'viewing',
        });
        if (cancelled) return;
        setThread((prev) => {
          if (!prev) return prev;
          const merged = { ...prev, ...res };
          // Our own in-flight answer is on screen from the stream already.
          if (streamingRef.current) merged.run = null;
          return merged;
        });
        pollRef.current = setTimeout(tick, res.pollMs || 20000);
      } catch {
        // A failed poll is not worth showing — the next one probably works.
        if (!cancelled) pollRef.current = setTimeout(tick, 30000);
      }
    };

    pollRef.current = setTimeout(tick, 1500);
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [activeId, paused]);

  // THE RAIL READS `threads`, THE HEADER READS `thread`, AND ONLY applyTurn
  // KEPT THE TWO IN STEP. The title is generated after the first turn and
  // reaches the client on that turn's response — which a long turn never
  // delivers (the ~45 s edge answers a bodyless 500 while the function carries
  // on). The title then arrived through reloadThread / the live poll, landed in
  // `thread`, and the rail went on saying "Untitled chat" beside a header that
  // named the chat. Whatever path brings a title in, the rail follows it.
  const threadTitle = thread?.conversation?.title;
  const threadId    = thread?.conversation?.id;
  useEffect(() => {
    if (!threadTitle || !threadId) return;
    setThreads((prev) => (prev.some(t => t.id === threadId && t.title !== threadTitle)
      ? prev.map(t => (t.id === threadId ? { ...t, title: threadTitle } : t))
      : prev));
  }, [threadTitle, threadId]);

  const messages = useMemo(() => thread?.messages || [], [thread]);
  const byId = useMemo(() => new Map(messages.map(m => [m.id, m])), [messages]);

  // The optimistic user turn is dropped as soon as the server's own copy shows
  // up — the server persists it before it calls the model, so it always does.
  const optimistic = useMemo(() => {
    if (!pendingText) return null;
    const landed = messages.some(m => m.role === 'user' && textOf(m) === pendingText);
    if (landed) return null;
    return {
      id: '__pending__', role: 'user', content: [{ type: 'text', text: pendingText }],
      text: pendingText, createdByName: 'You', createdAt: new Date().toISOString(), optimistic: true,
    };
  }, [pendingText, messages]);

  useEffect(() => {
    if (pendingText && messages.some(m => m.role === 'user' && textOf(m) === pendingText)) setPendingText(null);
  }, [messages, pendingText]);

  // Flatten the tree into render order, carrying each node's depth.
  // Grouped once per render rather than filtered per message — a thread of 40
  // turns would otherwise walk the list 40 times.
  const proposalsBy = useMemo(() => {
    const by = new Map();
    for (const d of proposals) {
      if (!d.messageId) continue;
      const list = by.get(d.messageId) || [];
      list.push(d);
      by.set(d.messageId, list);
    }
    return by;
  }, [proposals]);

  const rendered = useMemo(() => {
    const out = [];
    const walk = (nodes, depth) => {
      for (const node of (nodes || [])) {
        const message = byId.get(node.id);
        if (message) out.push({ message, depth });
        walk(node.children, depth + 1);
      }
    };
    walk(thread?.tree, 0);
    // A message the tree doesn't mention (a pre-160 row set has no tree) still
    // renders — the transcript is never allowed to silently lose a turn.
    if (!out.length) for (const m of messages) out.push({ message: m, depth: 0 });
    return out;
  }, [thread, byId, messages]);

  const shown = useMemo(
    () => (showAll || rendered.length <= VISIBLE_TAIL ? rendered : rendered.slice(-VISIBLE_TAIL)),
    [rendered, showAll]);

  // An orphan is any OPEN proposal that no rendered message will carry. Two
  // ways that happens: the attach was best-effort and failed, or the turn that
  // proposed it was later edited or regenerated, so it is superseded and no
  // longer in the tree. Either way a card nobody can find is a proposal nobody
  // declines, so it renders at the foot rather than not at all.
  const orphanProposals = useMemo(() => {
    const live = new Set(rendered.map(r => r.message.id));
    return proposals.filter(d => d.status === 'open' && (!d.messageId || !live.has(d.messageId)));
  }, [proposals, rendered]);

  // SCROLLING IS THE TRANSCRIPT'S OWN, and only when the reader is already at
  // the foot of it. scrollIntoView walked up to whichever ancestor happened to
  // scroll — the page, in the deal tab — and yanked somebody reading an older
  // turn down to the bottom every time a poll landed.
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [rendered.length, busy, streamText, activeId]);

  // The composer grows with what is typed, to a point — a two-line box for a
  // paragraph-long question is a box you cannot read your own question in.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 190)}px`;
  }, [draft]);

  const applyTurn = (res) => {
    setThread((prev) => ({ ...(prev || {}), ...res,
      conversation: { ...(prev?.conversation || {}), title: res.title || prev?.conversation?.title } }));
    setThreads((prev) => prev.map(t => (t.id === activeId
      ? { ...t, title: res.title || t.title, lastMessageAt: new Date().toISOString() }
      : t)));
    // A turn may have proposed something. The transcript comes back in the
    // response; the cards do not, because their status is server state.
    loadProposals();
  };

  // A rating is local to one message; the response carries only that message's
  // rating, so it is patched in place rather than reloading the thread.
  const setMessageFeedback = (messageId, myFeedback) => {
    setThread((prev) => (prev ? {
      ...prev,
      messages: (prev.messages || []).map(m => (m.id === messageId ? { ...m, myFeedback } : m)),
    } : prev));
  };

  const reloadThread = () => {
    if (!activeId) return;
    api.get(`conversations/${activeId}`).then(setThread).catch(() => {});
  };

  // entityId is omitted entirely for General rather than sent as null — the
  // API rejects a General ref that carries one at all.
  const newThreadBody = general ? { entityType: 'General' } : { entityType, entityId };

  const startThread = async () => {
    setError(null);
    try {
      const res = await api.post('conversations', newThreadBody);
      setThreads((prev) => [res.conversation, ...prev]);
      setActiveId(res.conversation.id);
      setThread(res);
      setListOpen(false);
      stickRef.current = true;
      textareaRef.current?.focus();
    } catch (e) { setError(e.message); }
  };

  // Only a deal or capital-item thread can file an attachment on Documents.
  const canSaveToDocs = !general && !!(dealId || dealDetailId);
  const needsDocsAnswer = canSaveToDocs && files.length > 0 && saveToDocs === null;

  const addFiles = (list) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    const problem = attachmentProblem(picked, files.length);
    if (problem) { setError(problem); return; }
    setError(null);
    setFiles(prev => [...prev, ...picked.map((file, i) => ({ key: `${Date.now()}-${i}-${file.name}`, file }))]);
  };
  const removeFile = (key) => setFiles(prev => {
    const next = prev.filter(f => f.key !== key);
    if (!next.length) setSaveToDocs(null);
    return next;
  });

  const openAttachment = async (message, a) => {
    if (!activeId) return;
    // Opened before the await, or the popup blocker takes it.
    const win = window.open('', '_blank');
    try {
      const res = await api.get(`conversations/${activeId}/messages/${message.id}/attachments/${a.index}`);
      if (win) win.location.href = res.url; else window.location.href = res.url;
    } catch (e) {
      if (win) win.close();
      setError(e.message);
    }
  };

  const send = async () => {
    const message = draft.trim();
    const sending = editing ? [] : files;
    if ((!message && !sending.length) || busy || (!editing && needsDocsAnswer)) return;

    let id = activeId;
    let current = thread;
    setBusy(true);
    setError(null);
    setNotice(null);
    stickRef.current = true;
    try {
      if (!id) {
        const created = await api.post('conversations', newThreadBody);
        id = created.conversation.id;
        current = created;
        setThreads((prev) => [created.conversation, ...prev]);
        setActiveId(id);
        setThread(created);
      }
      // Files upload BEFORE the question is sent: the message carries only
      // their references. A failure here leaves the draft and the files in
      // place, so nothing the person chose is lost.
      let attachments = [];
      if (sending.length) {
        for (let i = 0; i < sending.length; i++) {
          setUploading(`${i + 1} of ${sending.length}`);
          try {
            attachments.push(await uploadChatAttachment(api, {
              file: sending[i].file, conversationId: id,
              saveToDocuments: canSaveToDocs && saveToDocs === true,
              dealId, dealDetailId,
            }));
          } catch (err) {
            // Nothing was asked yet — this is not an answer still on its way.
            err.uploadFailed = true;
            throw err;
          }
        }
        setUploading(null);
      }

      setDraft('');
      setPendingText(message || null);
      if (sending.length) { setFiles([]); setSaveToDocs(null); }

      // Editing an existing turn goes to its own endpoint: the edit lands as a
      // revision, not as a new question at the bottom of the thread.
      const path = editing
        ? `conversations/${id}/messages/${editing.id}`
        : `conversations/${id}/messages`;
      const payload = editing
        ? { message }
        : { message, parentMessageId: replyTo?.id || null,
            ...(attachments.length ? { attachments } : {}) };

      if (thread?.streamEnabled) {
        await streamTurn(path, payload);
      } else {
        const res = editing
          ? await api.put(path, payload, { timeout: 300000 })
          : await api.post(path, payload, { timeout: 300000 });
        applyTurn(res);
      }

      setReplyTo(null);
      setEditing(null);
      setPendingText(null);
      if (!current) reloadThread();
    } catch (e) {
      // The user turn is saved server-side even when the answer failed — say so
      // rather than silently dropping what they typed.
      //
      // A 5xx or a timeout is usually NOT a failed turn: the Static Web Apps
      // edge closes a request at ~45 s with a bodyless 500 while the function
      // keeps answering, and a bulk filing turn routinely runs past that. The
      // live poll carries the transcript, so the answer lands here on its own.
      // Saying "Internal server error" over it is what made people ask again.
      if (e?.uploadFailed) {
        // Nothing was asked yet; the files and the draft are still in place.
        setError(e.message);
      } else if (answerMayStillArrive(e)) {
        setNotice('Harvi is still working on this — the answer will appear here when it is ready. No need to ask again.');
      } else {
        setError(e.message);
      }
      setPendingText(null);
      setUploading(null);
      reloadThread();
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const text = String(askRequest?.text || '').trim();
    if (!text) return;
    askingRef.current = text;
    setReplyTo(null);
    setEditing(null);
    setActiveId(null);
    setListOpen(false);
    setDraft(text);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askRequest?.n]);
  useEffect(() => {
    const text = askingRef.current;
    if (!text || busy || activeId || draft.trim() !== text) return;
    askingRef.current = null;
    send();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, activeId, busy]);

  // Run one turn over SSE. The `done` frame carries only ids and usage, so the
  // persisted messages, tree and versions come from a reload afterwards — which
  // is also what repairs the view if the stream died part-way.
  const streamTurn = async (path, payload) => {
    streamingRef.current = true;
    setStreamText('');
    let failed = null;
    try {
      const { streamed, data } = await api.postStream(`${path}?stream=1`, payload, {
        onEvent: (evt) => {
          const kind = evt.event;
          if (kind === 'delta' && evt.data?.text) {
            setStreamText((t) => (t || '') + evt.data.text);
          } else if (kind === 'error') {
            failed = evt.data?.message || 'The assistant could not answer that turn.';
          }
        },
      });
      // The server answered with plain JSON after all — the turn happened, so
      // use that response rather than asking again.
      if (!streamed) { applyTurn(data); return; }
      if (failed) setError(failed);
      reloadThread();
    } finally {
      streamingRef.current = false;
      setStreamText(null);
    }
  };

  const regenerate = async (message) => {
    if (!activeId || busy) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const path = `conversations/${activeId}/messages/${message.id}/regenerate`;
      if (thread?.streamEnabled) await streamTurn(path, {});
      else applyTurn(await api.post(path, {}, { timeout: 300000 }));
    } catch (e) {
      setError(e.message);
      reloadThread();
    } finally { setBusy(false); }
  };

  const beginEdit = (message) => {
    setEditing(message);
    setReplyTo(null);
    setDraft(textOf(message));
    textareaRef.current?.focus();
  };

  // THE PER-CHAT WEB RESEARCH SWITCH. true / false pin it for this thread; null
  // hands it back to the person's own default (Notification settings → Harvi).
  // The server answers with the resolved state (setting / default / effective)
  // and the header renders that verbatim, so what the badge says and what the
  // next turn gets cannot disagree.
  const changeWebResearch = async (settingNext) => {
    if (!activeId) return;
    try {
      const res = await api.put(`conversations/${activeId}`, { webResearch: settingNext });
      setThread((prev) => ({ ...prev, conversation: { ...prev.conversation, ...res.conversation }, webResearch: res.webResearch }));
    } catch (e) { setError(e.message); }
  };

  const changeVisibility = async (visibilityNext) => {
    if (!activeId) return;
    try {
      const res = await api.put(`conversations/${activeId}`, { visibility: visibilityNext });
      setThread((prev) => ({ ...prev, conversation: { ...prev.conversation, ...res.conversation } }));
      setThreads((prev) => prev.map(t => (t.id === activeId ? { ...t, visibility: visibilityNext } : t)));
    } catch (e) { setError(e.message); }
  };

  // A delete is a SOFT delete server-side (IsDeleted = 1) and only the author
  // or an Admin may do it. It removes the thread for EVERYONE who can read it,
  // which on a deal thread is the whole team — hence a confirm that says so.
  const deleteThread = async () => {
    if (!activeId) return;
    setDeleting(true);
    try {
      await api.del(`conversations/${activeId}`);
      const gone = activeId;
      setThreads((prev) => {
        const rest = prev.filter(t => t.id !== gone);
        setActiveId(rest[0]?.id || null);
        return rest;
      });
      setConfirmDelete(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
    }
  };

  // Promotion is one-way and it publishes a transcript, so it is confirmed in
  // words that say what will happen rather than "Are you sure?". Nothing in a
  // shared thread is deletable — an edit supersedes and the original stays — so
  // there is no undo to fall back on. THE CONFIRMATION IS IN THE PAGE, not a
  // window.confirm: a native dialog in the middle of a designed panel is a jolt,
  // and it cannot show the sentence in the same words the card already used.
  const promote = async (offer) => {
    if (!activeId || !offer || promoting) return;
    setPromoting(true); setError(null); setNotice(null);
    try {
      const res = await api.post(`conversations/${activeId}/promote`,
        { entityType: offer.entityType, entityId: offer.entityId });
      setNotice(`This thread is now on ${offer.name}.`);
      setConfirmPromote(false);
      // Re-read rather than patch: promotion changes the thread's entity, so
      // its access, its label and its offer are all now different answers.
      const fresh = await api.get(`conversations/${activeId}`);
      setThread(fresh);
      setThreads((prev) => prev.map(t => (t.id === activeId
        ? { ...t, visibility: res.conversation?.visibility || 'Entity' } : t)));
    } catch (e) { setError(e.message); } finally { setPromoting(false); }
  };

  const changeModel = async (model) => {
    if (!activeId) return;
    try {
      const res = await api.put(`conversations/${activeId}`, { model });
      setThread((prev) => ({ ...prev, conversation: { ...prev.conversation, ...res.conversation } }));
    } catch (e) { setError(e.message); }
  };

  const changeEntities = async (entities) => {
    if (!activeId) return;
    setError(null);
    try {
      const res = await api.put(`conversations/${activeId}/entities`, { entities });
      setThread((prev) => ({ ...prev, entities: res.entities }));
      setThreads((prev) => prev.map(t => (t.id === activeId
        ? { ...t, linkedCount: (res.entities || []).filter(e => !e.primary).length } : t)));
    } catch (e) { setError(e.message); }
  };

  const exportPdf = async () => {
    if (!activeId || exporting) return;
    setExporting(true); setError(null); setNotice(null);
    try {
      const res = await exportConversationPdf(activeId);
      setNotice(`Exported ${res.messages} message${res.messages === 1 ? '' : 's'}${res.truncated ? ' (truncated)' : ''}.`);
    } catch (e) { setError(e.message); }
    finally { setExporting(false); }
  };

  const conversation = thread?.conversation;
  const canEditThread = conversation?.canEdit !== false;
  // Web research, as the server resolved it for this thread and this reader.
  const webResearch = thread?.webResearch || null;
  const webOn = webResearch?.effective === true;
  // THE PLACEHOLDER MUST MATCH defaultVisibilityFor, not the record default.
  // Before a thread exists there is no row to read, and falling back to
  // 'Entity' told somebody on the General surface their thread was visible to
  // every Harvus user when the one they are about to create will be Private —
  // a header contradicting the empty state directly beneath it, and a claim
  // about the audience made to the person deciding what to type.
  const visibility = conversation?.visibility || (general ? 'Private' : 'Entity');
  // Auto deal tags widen the audience of an 'Entity' thread (access to a
  // tagged deal is enough), so the header line has to know how many there are.
  const taggedCount = (thread?.entities || []).filter(e => !e.primary && e.source === 'auto').length;
  const models = thread?.models || ['claude-sonnet-5', 'claude-opus-5'];
  // ON A GENERAL THREAD 'Entity' AND 'Org' ARE THE SAME AUDIENCE, because
  // canAccessEntity answers ok:true for any authenticated caller on a General
  // thread — there is no record to scope to. Offering both would be two
  // controls for one outcome, and the one labelled "everyone with access to
  // this workspace" reads narrower than it is. So General offers Private and
  // Org only; an existing 'Entity' thread keeps a matching option so the
  // select never renders a value it does not contain.
  const visibilityOptions = general
    ? [
      { value: 'Private', label: 'Only me' },
      { value: 'Org',     label: 'Every Harvus user' },
      ...(visibility === 'Entity' ? [{ value: 'Entity', label: 'Every Harvus user (legacy)' }] : []),
    ]
    : [
      { value: 'Entity',  label: `Everyone with access to this ${entityLabel}` },
      { value: 'Private', label: 'Only me' },
      { value: 'Org',     label: 'Every Harvus user' },
    ];
  const features = thread?.features || {};
  const run = thread?.run;
  const activeModel = conversation?.model || thread?.defaultModel || models[0];
  const starters = general ? STARTERS.general : STARTERS.record;

  const applyStarter = (text) => {
    setDraft(text);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  // ── Chrome pieces ─────────────────────────────────────────────────────────
  const toggleRail = () => (roomForRail ? setRailHidden(v => !v) : setListOpen(o => !o));
  const threadsButton = (
    <button
      type="button"
      onClick={toggleRail}
      aria-expanded={showRail || listOpen}
      aria-label={`Chats (${threads.length})`}
      title={showRail ? 'Hide chats' : 'Show chats'}
      style={{ ...headerIconBtn, flexShrink: 0 }}
      {...hoverSwap({ background: B.hover, color: B.text }, { background: 'none', color: B.textMd })}
    >
      <Icon name="sidebar" size={15} />
    </button>
  );

  const railPane = (
    <ThreadList
      threads={threads} activeId={activeId} loading={listLoading} general={general}
      onSelect={(id) => { setActiveId(id); setListOpen(false); stickRef.current = true; }}
      onNew={startThread}
    />
  );

  const transcript = (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      style={{
        flex: fill ? 1 : undefined,
        minHeight: 0,
        maxHeight: fill ? undefined : bodyMaxHeight,
        overflowY: 'auto',
        background: B.surface,
      }}
    >
      {/* The SCROLLER is full width and the CONTENT is a column: a scrollbar
          floating in the middle of the panel reads as a nested pane. */}
      <div style={{ maxWidth: COLUMN, margin: '0 auto', padding: `0 ${space.lg}px ${space.sm}px` }}>
      {threadLoading && <ThinkingDots label="Loading thread…" />}

      {!threadLoading && !activeId && (
        <div style={{ padding: `${space.xxl}px ${space.sm}px`, textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: space.md }}>
            <HarviMark size={44} />
          </div>
          <div style={{ fontSize: font.lg, fontWeight: weight.semibold, color: B.text, letterSpacing: -0.2 }}>
            {general ? 'Ask Harvi anything' : 'Ask about this record'}
          </div>
          <div style={{ fontSize: font.sm, color: B.textMd, maxWidth: 420, margin: '6px auto 0', lineHeight: 1.55 }}>
            {general
              ? 'Threads here start private to you. Ask about a deal, a loan, a task or a document — and if a thread turns out to be about one deal, Harvi offers to put it on that deal for the team.'
              : 'Threads here are shared: anyone with access to this record can read and reply. Harvi can search the filed documents to answer.'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: space.lg }}>
            {starters.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => applyStarter(s)}
                style={{
                  background: B.surface, border: `1px solid ${B.divider}`, borderRadius: radius.pill,
                  padding: '7px 14px', cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: font.sm, color: B.navy, boxShadow: B.shadowXs,
                  transition: 'background .15s, border-color .15s, transform .12s',
                }}
                {...hoverSwap(
                  { background: B.accentLt, borderColor: B.accent + '55', transform: 'translateY(-1px)' },
                  { background: B.surface, borderColor: B.divider, transform: 'translateY(0)' })}
              >{s}</button>
            ))}
          </div>
        </div>
      )}

      {!threadLoading && activeId && !rendered.length && !optimistic && (
        <div style={{ padding: `${space.xl}px 0`, textAlign: 'center' }}>
          <div style={{ color: B.textMd, fontSize: font.base, marginBottom: space.md }}>
            Nothing here yet — ask the first question.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {starters.map((s) => (
              <button
                key={s} type="button" onClick={() => applyStarter(s)}
                style={{
                  background: B.surface, border: `1px solid ${B.divider}`, borderRadius: radius.pill,
                  padding: '6px 13px', cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: font.sm, color: B.navy,
                }}
                {...hoverSwap({ background: B.accentLt }, { background: B.surface })}
              >{s}</button>
            ))}
          </div>
        </div>
      )}

      {!showAll && rendered.length > VISIBLE_TAIL && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: `${space.md}px 0 0` }}>
          <Btn size="sm" variant="ghost" onClick={() => setShowAll(true)}>
            Show {rendered.length - VISIBLE_TAIL} earlier message{rendered.length - VISIBLE_TAIL === 1 ? '' : 's'}
          </Btn>
        </div>
      )}

      {shown.map(({ message, depth }, i) => {
        const prev = shown[i - 1];
        // Consecutive turns from the same speaker at the same depth drop the
        // avatar and the name: a run of three answers reads as one voice.
        const grouped = !!prev
          && prev.depth === depth
          && prev.message.role === message.role
          && (prev.message.createdByName || '') === (message.createdByName || '');
        return (
          <MessageRow
            key={message.id}
            message={message}
            depth={depth}
            grouped={grouped}
            busy={busy}
            features={features}
            onReply={(m) => { setReplyTo(m); setEditing(null); textareaRef.current?.focus(); }}
            onEdit={beginEdit}
            onRegenerate={regenerate}
            onOpenAttachment={openAttachment}
            conversationId={activeId}
            onFeedback={setMessageFeedback}
            proposals={proposalsBy.get(message.id) || EMPTY}
            assignees={assignees}
            onProposalChanged={loadProposals}
            draftTarget={draftTarget}
          />
        );
      })}

      {/* A proposal whose message could not be attached — the bookkeeping
          is best-effort, so it renders at the foot rather than not at
          all. A card nobody can find is a proposal nobody declines. */}
      {orphanProposals.map((d) => (
        <AssistantDraftCard key={d.draftId} draft={d} assignees={assignees} onChanged={loadProposals} defaultTarget={draftTarget} />
      ))}

      {optimistic && <MessageRow message={optimistic} depth={0} features={features} />}

      {streamText !== null && (
        <MessageRow
          depth={0}
          features={features}
          pending
          message={{
            id: '__streaming__', role: 'assistant',
            content: [{ type: 'text', text: streamText }], text: streamText,
            createdAt: new Date().toISOString(),
          }}
        />
      )}

      {/* Someone else's in-flight answer — this is what makes a streaming
          response a SHARED view rather than a private one. */}
      {run && (
        <div style={{ display: 'flex', gap: 12, padding: `${space.lg}px 0 0` }}>
          <HarviMark />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: font.sm, color: B.textMd, marginBottom: 4 }}>
              {run.startedByName || 'Someone'} asked something — Harvi is answering
              {run.kind === 'regenerate' ? ' again' : ''}…
            </div>
            {run.partialText
              ? <div><MessageBody text={run.partialText} muted /><Caret /></div>
              : <ThinkingDots />}
          </div>
        </div>
      )}

      {busy && !run && streamText === null && <ThinkingDots label="Harvi is reading the file…" />}
      </div>
    </div>
  );

  const canSend = !busy && (editing
    ? !!draft.trim()
    : (!!draft.trim() || files.length > 0) && !needsDocsAnswer);

  const composer = (
    <div style={{
      flexShrink: 0, padding: `${space.sm}px ${space.lg}px ${space.md}px`,
      background: B.surface,
    }}>
      <div style={{ maxWidth: COLUMN, margin: '0 auto' }}>
      {(replyTo || editing) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '6px 6px 6px 10px',
          background: B.accentLt, borderRadius: radius.pill, fontSize: font.xs, color: B.navyDk,
        }}>
          <Icon name={editing ? 'edit' : 'message'} size={12} />
          <span style={{ fontWeight: weight.semibold, flexShrink: 0 }}>
            {editing ? 'Editing your question' : `Replying to ${replyTo.createdByName || 'Harvi'}`}
          </span>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.75 }}>
            {textOf(editing || replyTo).slice(0, 120)}
          </span>
          <button
            type="button"
            aria-label="Cancel"
            onClick={() => { setReplyTo(null); setEditing(null); if (editing) setDraft(''); }}
            style={{ display: 'flex', background: 'none', border: 'none', cursor: 'pointer', color: B.navyDk, padding: 2 }}
          ><Icon name="close" size={13} /></button>
        </div>
      )}

      {/* THE COMPOSER IS A CARD, AND THE MODEL PICKER LIVES IN IT. Which model
          answers is a decision made per question, at the moment of asking —
          buried in a settings menu it is a setting somebody configures once and
          then forgets they are paying Opus rates for. Text on top, controls
          along the foot: the input grows downward without the buttons moving. */}
      <div style={{
        border: `1px solid ${composerFocus ? B.accent : B.border}`, borderRadius: radius.xxxl,
        background: B.surface, boxShadow: composerFocus ? B.ringAccent : B.shadowXs,
        transition: 'border-color .15s, box-shadow .15s',
        padding: `${space.sm}px ${space.md}px ${space.sm - 2}px`,
      }}
        onDragOver={(e) => { if (!editing && e.dataTransfer?.types?.includes('Files')) e.preventDefault(); }}
        onDrop={(e) => { if (editing || !e.dataTransfer?.files?.length) return; e.preventDefault(); addFiles(e.dataTransfer.files); }}
      >
        {files.length > 0 && !editing && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {files.map(({ key, file }) => (
                <span key={key} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 260,
                  padding: '4px 6px 4px 10px', borderRadius: radius.pill,
                  background: B.surfaceMuted, border: `1px solid ${B.divider}`, fontSize: font.xs, color: B.text,
                }}>
                  <Icon name="attach" size={12} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                  <span style={{ color: B.textLt, flexShrink: 0 }}>{fmtBytes(file.size)}</span>
                  <button
                    type="button" aria-label={`Remove ${file.name}`} disabled={busy}
                    onClick={() => removeFile(key)}
                    style={{ display: 'flex', background: 'none', border: 'none', cursor: 'pointer', color: B.textMd, padding: 2 }}
                  ><Icon name="close" size={11} /></button>
                </span>
              ))}
            </div>
            {/* THE QUESTION IS ASKED, NOT DEFAULTED. Filing a file on the deal
                puts it in front of everyone on the deal and through OCR, the
                summary and SharePoint; keeping it in the chat does none of that.
                Neither is the safe default, so Send waits for an answer. */}
            {canSaveToDocs && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8,
                padding: '8px 10px', borderRadius: radius.xl,
                background: saveToDocs === null ? B.accentLt : B.surfaceMuted,
                fontSize: font.xs, color: B.navyDk,
              }}>
                <span style={{ fontWeight: weight.semibold }}>
                  {files.length === 1 ? 'Also add this file' : `Also add these ${files.length} files`} to this {entityLabel}'s Documents?
                </span>
                <span style={{ flex: 1 }} />
                {[[true, 'Yes, add to Documents'], [false, 'No, chat only']].map(([value, label]) => (
                  <button
                    key={label} type="button" aria-pressed={saveToDocs === value} disabled={busy}
                    onClick={() => setSaveToDocs(value)}
                    style={{
                      padding: '4px 10px', borderRadius: radius.pill, fontFamily: 'inherit', fontSize: font.xs,
                      fontWeight: weight.medium, cursor: busy ? 'not-allowed' : 'pointer',
                      border: `1px solid ${saveToDocs === value ? B.navy : B.border}`,
                      background: saveToDocs === value ? B.navy : B.surface,
                      color: saveToDocs === value ? B.white : B.text,
                    }}
                  >{label}</button>
                ))}
              </div>
            )}
          </div>
        )}
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setComposerFocus(true)}
          onBlur={() => setComposerFocus(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
          }}
          rows={1}
          placeholder={editing
            ? 'Rewrite your question — the original stays in the thread'
            : general
              ? 'How can Harvi help?'
              : `Ask about this ${entityLabel}…`}
          disabled={busy}
          style={{
            display: 'block', width: '100%', resize: 'none', minHeight: 24, maxHeight: 190,
            padding: '3px 0', border: 'none', outline: 'none', background: 'transparent',
            fontFamily: 'inherit', fontSize: font.md, lineHeight: 1.5, color: B.text,
            boxSizing: 'border-box',
          }}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, marginTop: 4 }}>
          {activeId && canEditThread && models.length > 1 && (
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setModelOpen(o => !o)}
                aria-haspopup="menu"
                aria-expanded={modelOpen}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: 'none', border: 'none', borderRadius: radius.lg,
                  padding: '4px 7px', cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: font.xs, fontWeight: weight.medium, color: B.textMd,
                  transition: 'background .15s, color .15s',
                }}
                {...hoverSwap({ background: B.hover, color: B.text }, { background: 'none', color: B.textMd })}
              >
                {MODEL_LABELS[activeModel] || activeModel}
                <Icon name="chevronDown" size={11} />
              </button>
              {/* Upward, because the composer sits at the foot of the panel. */}
              <Popover open={modelOpen} onClose={() => setModelOpen(false)} width={260} placement="up" align="left">
                <MenuLabel>Model</MenuLabel>
                {models.map(m => (
                  <MenuOption
                    key={m} title={MODEL_LABELS[m] || m} blurb={MODEL_BLURBS[m]}
                    selected={activeModel === m}
                    onClick={() => { changeModel(m); setModelOpen(false); }}
                  />
                ))}
              </Popover>
            </div>
          )}

          {!editing && (
            <>
              <input
                ref={fileInputRef} type="file" multiple hidden
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy || files.length >= MAX_CHAT_FILES}
                aria-label="Attach files"
                title="Attach files"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 28, height: 28, background: 'none', border: 'none', borderRadius: radius.lg,
                  cursor: busy ? 'not-allowed' : 'pointer', color: B.textMd,
                }}
                {...hoverSwap({ background: B.hover, color: B.text }, { background: 'none', color: B.textMd })}
              ><Icon name="attach" size={15} /></button>
            </>
          )}
          {uploading && <span style={{ fontSize: font.xs, color: B.textMd }}>Uploading {uploading}…</span>}
          {!uploading && needsDocsAnswer && !editing && (
            <span style={{ fontSize: font.xs, color: B.textMd }}>Choose where the files go to send</span>
          )}

          <span style={{ flex: 1 }} />

          <button
            type="button"
            onClick={send}
            disabled={!canSend}
            aria-label={editing ? 'Save and re-ask' : 'Send'}
            title={editing ? 'Save & re-ask' : 'Send (⌘/Ctrl + Enter)'}
            style={{
              width: 32, height: 32, flexShrink: 0, borderRadius: '50%', border: 'none',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: !canSend ? B.surfaceSunken : B.navy,
              backgroundImage: !canSend ? 'none' : B.gradPrimary,
              color: !canSend ? B.textLt : B.white,
              cursor: !canSend ? 'not-allowed' : 'pointer',
              transition: 'background .15s, color .15s',
            }}
          >
            <Icon name={editing ? 'check' : 'arrowUp'} size={16} />
          </button>
        </div>
      </div>

      <div style={{ fontSize: font.xs, color: B.textLt, marginTop: 7, textAlign: 'center' }}>
        Harvi can be wrong — check the cited record before you act on a number.
      </div>
      </div>
    </div>
  );

  return (
    <div
      ref={rootRef}
      style={{
        display: 'flex', flexDirection: 'column',
        height: fill ? '100%' : undefined, minHeight: 0,
        // In `fill` the host (the floating widget) already draws the frame, so
        // drawing a second one inside it puts a rounded card inside a rounded
        // card with a hairline between them. The panel is a card on a page and
        // a plain column inside a shell.
        border: fill ? 'none' : `1px solid ${B.divider}`,
        borderRadius: fill ? 0 : radius.xxxl,
        boxShadow: fill ? 'none' : B.shadowSm,
        background: B.surface, overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', flex: fill ? 1 : undefined, minHeight: 0, position: 'relative' }}>
        {/* Thread rail — persistent when there is room for it. */}
        {showRail && (
          <div style={{
            width: 244, flexShrink: 0, borderRight: `1px solid ${B.divider}`,
            background: B.surfaceMuted, overflowY: 'auto', minHeight: 0,
          }}>{railPane}</div>
        )}

        {/* Narrow container: the same list as an overlay drawer. */}
        {!showRail && listOpen && (
          <>
            <div
              onClick={() => setListOpen(false)}
              style={{
                position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
                background: 'rgba(22,42,50,0.25)', zIndex: 20,
              }}
            />
            <div style={{
              position: 'absolute', top: 0, left: 0, bottom: 0, width: 244, zIndex: 21,
              background: B.surface, borderRight: `1px solid ${B.divider}`, boxShadow: B.shadowLg,
              overflowY: 'auto', animation: 'harvus-slide-in-left .18s ease-out',
            }}>{railPane}</div>
          </>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
          {/* ── Header ─────────────────────────────────────────────────────── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: space.sm, flexShrink: 0,
            padding: `${space.md}px ${space.lg}px`, borderBottom: `1px solid ${B.divider}`,
            background: B.surface,
          }}>
            {threadsButton}
            {!showRail && (
              <button
                type="button" onClick={startThread} aria-label="New chat" title="New chat"
                style={{ ...headerIconBtn, flexShrink: 0 }}
                {...hoverSwap({ background: B.hover, color: B.text }, { background: 'none', color: B.textMd })}
              ><Icon name="edit" size={15} /></button>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontWeight: weight.semibold, fontSize: font.md, color: B.text, letterSpacing: -0.15,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {conversation?.title || (activeId ? 'Untitled chat' : 'New chat')}
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, marginTop: 2,
                fontSize: font.xs, color: B.textLt,
                flexWrap: compact ? 'nowrap' : 'wrap', minWidth: 0,
              }}>
                {visibility === 'Private' && <Icon name="lock" size={10} />}
                <span style={compact ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : undefined}>
                  {visibilityCopy(visibility, entityType, entityLabel, taggedCount)}
                </span>
                {!compact && visibility !== 'Private' && <span>· anyone who can read it can reply</span>}
              </div>
            </div>

            {!compact && thread?.mcpEnabled && <Badge color={B.green}>Docs</Badge>}
            {!compact && webOn && (
              <span title="Harvi may search the public internet in this chat" style={{ display: 'inline-flex' }}>
                <Badge color={B.accent}>Web</Badge>
              </span>
            )}
            {activeId && (
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => setMenuOpen(o => !o)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-label="Thread settings"
                  style={headerIconBtn}
                  {...hoverSwap({ background: B.hover, color: B.text }, { background: 'none', color: B.textMd })}
                >
                  <Icon name="moreVertical" size={15} />
                </button>
                <Popover open={menuOpen} onClose={() => setMenuOpen(false)}>
                  {canEditThread && (
                    <>
                      <MenuLabel>Who can see this</MenuLabel>
                      {visibilityOptions.map(o => (
                        <MenuOption
                          key={o.value} title={o.label} selected={visibility === o.value}
                          onClick={() => { changeVisibility(o.value); setMenuOpen(false); }}
                        />
                      ))}
                      <div style={{ height: 1, background: B.divider, margin: `${space.sm}px 4px` }} />
                    </>
                  )}
                  {webResearch?.available && webResearch.perThread && (
                    <>
                      <MenuLabel>Web research</MenuLabel>
                      <MenuOption
                        title={`Follow my default (${webResearch.default ? 'on' : 'off'})`}
                        blurb="Set under Notification settings → Harvi."
                        selected={webResearch.setting === null}
                        onClick={() => { changeWebResearch(null); setMenuOpen(false); }}
                      />
                      <MenuOption
                        title="On for this chat"
                        blurb="Harvi may search the public internet; every web fact is attributed to its page."
                        selected={webResearch.setting === true}
                        onClick={() => { changeWebResearch(true); setMenuOpen(false); }}
                      />
                      <MenuOption
                        title="Off for this chat"
                        blurb="Harvus records only."
                        selected={webResearch.setting === false}
                        onClick={() => { changeWebResearch(false); setMenuOpen(false); }}
                      />
                      <div style={{ height: 1, background: B.divider, margin: `${space.sm}px 4px` }} />
                    </>
                  )}
                  <MenuAction icon="download" disabled={exporting} onClick={() => { setMenuOpen(false); exportPdf(); }}>
                    {exporting ? 'Exporting…' : 'Export as PDF'}
                  </MenuAction>
                  {canEditThread && (
                    <MenuAction icon="trash" onClick={() => { setMenuOpen(false); setConfirmDelete(true); }}>
                      Delete chat
                    </MenuAction>
                  )}
                </Popover>
              </div>
            )}
          </div>

          {/* ── Notices ────────────────────────────────────────────────────── */}
          {(error || notice || conversation?.hasPersonalData || conversation?.promotedUTC
            || thread?.promotionOffer
            || (activeId && features.crossEntity) || (thread?.presence || []).some(p => !p.isSelf)) && (
            <div style={{
              flexShrink: 0, display: 'flex', flexDirection: 'column', gap: space.sm,
              padding: `${space.md}px ${space.lg}px 0`,
              // Same measure as the transcript and the composer, so a banner
              // does not start to the left of the conversation it is about.
              width: '100%', maxWidth: COLUMN, margin: '0 auto', boxSizing: 'border-box',
            }}>
              {error && <ErrorMsg msg={error} />}
              {notice && (
                <Callout icon="success" tone={B.green} tint={B.green + '12'}>{notice}</Callout>
              )}

              {/* THE OFFER. Present only when the server says the thread is about
                  exactly one deal, is the caller's, is not already on a record and
                  has not read anything personal — so this renders the decision, it
                  does not make it. */}
              {confirmDelete && (
                <Callout
                  icon="trash" tone={B.red} tint={`${B.red}12`}
                  actions={[
                    <Btn key="del" size="sm" variant="danger" onClick={deleteThread} disabled={deleting} loading={deleting}>
                      Delete chat
                    </Btn>,
                    <Btn key="back" size="sm" variant="ghost" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                      Cancel
                    </Btn>,
                  ]}
                >
                  {visibility === 'Private'
                    ? <>Delete <strong>{conversation?.title || 'this chat'}</strong>? It will disappear from your chat list.</>
                    : <>Delete <strong>{conversation?.title || 'this chat'}</strong> for <strong>everyone</strong> who can see it? It will disappear from their chat lists too.</>}
                </Callout>
              )}
              {thread?.promotionOffer && (
                <Callout
                  icon="sparkles" tone={B.accent} tint={B.accentLt}
                  actions={confirmPromote ? [
                    <Btn key="go" size="sm" onClick={() => promote(thread.promotionOffer)} disabled={promoting} loading={promoting}>
                      Yes, put it on {thread.promotionOffer.name}
                    </Btn>,
                    <Btn key="back" size="sm" variant="ghost" onClick={() => setConfirmPromote(false)} disabled={promoting}>
                      Cancel
                    </Btn>,
                  ] : [
                    <Btn key="ask" size="sm" onClick={() => setConfirmPromote(true)}>
                      Put on {thread.promotionOffer.name}
                    </Btn>,
                    <Btn key="keep" size="sm" variant="ghost" onClick={() => setThread(p => ({ ...p, promotionOffer: null }))}>
                      Keep private
                    </Btn>,
                  ]}
                >
                  {confirmPromote ? (
                    <>
                      Everyone with access to <strong>{thread.promotionOffer.name}</strong> will be able to read
                      this whole conversation and reply to it. <strong>This cannot be undone.</strong>
                    </>
                  ) : (
                    <>
                      This thread is all about <strong>{thread.promotionOffer.name}</strong>. Put it on the
                      {' '}{thread.promotionOffer.entityType === 'Deal' ? 'deal' : 'capital item'} so the team can see it?
                    </>
                  )}
                </Callout>
              )}

              {/* PERMANENTLY private is a different state from private, and the
                  person deciding what to type needs to know which one they are in.
                  A thread that has read a mailbox can never be shared, so saying
                  only "Visible only to you" understates the guarantee. */}
              {conversation?.hasPersonalData && (
                <Callout icon="lock" tone={B.amber} tint={B.amber + '12'}>
                  This thread stays private permanently
                  {conversation.personalDataSource ? ` — it has read ${conversation.personalDataSource}` : ''}.
                  {' '}Ask Harvi to draft a note if something here belongs on a deal.
                </Callout>
              )}

              {conversation?.promotedUTC && (
                <div style={{ fontSize: font.xs, color: B.textLt }}>
                  Moved onto this record from a private thread.
                </div>
              )}

              {activeId && features.crossEntity && (
                <ComparisonBar entities={thread?.entities} onChange={changeEntities} disabled={busy} />
              )}

              <PresenceRow presence={thread?.presence} />
            </div>
          )}

          {transcript}
          {composer}
        </div>
      </div>
    </div>
  );
};


/** A send error that the gateway, not the turn, produced — see `send`. */
export function answerMayStillArrive(err) {
  const status = Number(err?.status);
  if (status >= 500) return true;
  return !status && /timed out|timeout|network|failed to fetch/i.test(String(err?.message || ''));
}

export default ChatPanel;
